import React, { useMemo } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { CalendarDots } from "@phosphor-icons/react/dist/csr/CalendarDots";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { ActivityState } from "../../../graphql/generated/graphql";
import { Clock } from "@phosphor-icons/react";

function AxioResourceCard({
  title,
  icon,
  description,
  videoUrl,
  files,
  isHovered,
  onPress,
  onHoverIn,
  onHoverOut,
  dueDate,
  activityState,
  scheduledDate,
}) {
  const getResourceTypeText = () => {
    const hasVideo = !!videoUrl;
    const hasFiles = files && files.length > 0;

    if (hasVideo && hasFiles) return "Files & Video";
    if (hasVideo) return "Video";
    if (hasFiles) return "Files";
    return null;
  };

  const formatDueDate = (dateString) => {
    if (!dateString) return null;

    const dueDate = new Date(dateString);
    const today = new Date();

    const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return {
      text: `Due ${month}/${day}`,
      isOverdue: dueDateStart < todayStart,
    };
  };

  const formattedScheduledDateString = useMemo(() => {
    if (!scheduledDate) return "Locked until further notice";
    const dueDate = new Date(scheduledDate);

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return `Unlocks ${month}/${day}`;
  }, [scheduledDate]);

  const resourceType = getResourceTypeText();
  const dueDateInfo = formatDueDate(dueDate);

  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  // hide if draft
  if (activityState === ActivityState.Draft) {
    return null;
  }

  return (
    <Pressable
      onPress={isModuleLocked ? undefined : onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      style={({ pressed }) => [
        styles.resourceItem,
        !isModuleLocked && isHovered && styles.resourceItemHovered,
        pressed && styles.resourceItemPressed,
        isModuleLocked && styles.resourceItemLocked,
      ]}>
      <View style={styles.resourceItemLeft}>
        <View style={styles.textContainer}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
              {icon && <View style={{ marginRight: 10 }}>{icon}</View>}
              <CustomText
                text={title}
                weight="bold"
                style={[styles.resourceItemTitle, isModuleLocked && styles.resourceItemTitleLocked]}
                useTranslationText={false}
              />
            </View>
            <CustomSpacing type="horizontal" size="m" />
            {!isModuleLocked && dueDateInfo && (
              <View style={[styles.dueDatePill, dueDateInfo.isOverdue && styles.overduePill]}>
                <CalendarDots
                  size={14}
                  color={dueDateInfo.isOverdue ? "#DC2626" : "#344054"}
                  style={styles.calendarIcon}
                />
                <CustomText
                  weight="bold"
                  text={dueDateInfo.text}
                  size="s"
                  style={[styles.dueDateText, dueDateInfo.isOverdue && styles.overdueText]}
                  useTranslationText={false}
                />
              </View>
            )}
            {isModuleLocked && (
              <View style={styles.dueDatePill}>
                <Clock size={14} color={"#344054"} style={styles.calendarIcon} />
                <CustomText
                  weight="bold"
                  text={formattedScheduledDateString}
                  size="s"
                  style={styles.dueDateText}
                  useTranslationText={false}
                />
              </View>
            )}
          </View>

          {resourceType && (
            <CustomText
              text={resourceType}
              style={styles.resourceItemDescription}
              useTranslationText={false}
              numberOfLines={1}
            />
          )}
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  resourceItem: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  resourceItemLocked: {
    cursor: "auto",
  },
  resourceItemHovered: {
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  resourceItemPressed: {
    backgroundColor: "#F5F5F5",
  },
  resourceItemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  textContainer: {
    flex: 1,
  },
  resourceItemTitle: {
    alignSelf: "center",
  },
  resourceItemTitleLocked: {
    opacity: 0.5,
  },
  resourceItemDescription: {
    fontSize: 12,
    lineHeight: 16,
    color: "#667085",
  },
  dueDatePill: {
    backgroundColor: "#EAECF0",
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 360,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  overduePill: {
    backgroundColor: "#FEE2E2",
  },
  calendarIcon: {
    marginRight: 2,
  },
  dueDateText: {
    color: "#667085",
    fontSize: 12,
    fontWeight: "500",
  },
  overdueText: {
    color: "#DC2626",
  },
});

export default AxioResourceCard;
